import React, { Component } from 'react';
import Modal from 'react-modal';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import services from '@C/Reports/_utils/data';
import { formatCurrency, TableGrid } from '@C/TableGrid';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { DateRangeFilter } from '@C/Utils';

import './styles.scoped.scss';

const col = [
  {
    name: 'Date',
    key: 'date',
    sortable: true,
    type: 'date',
  },
  {
    name: 'Narration',
    key: 'narration',
  },
  {
    name: 'Credit',
    key: 'all',
    type: 'currency',
    formatter(props) {
      return <>{formatCurrency(props.credit)}</>;
    },
  },
  {
    name: 'Debit',
    key: 'all',
    type: 'currency',
    formatter(props) {
      return <>{formatCurrency(props.debit)}</>;
    },
  },
  {
    name: 'Balance',
    key: 'all',
    type: 'currency',
    formatter(props) {
      return <>{formatCurrency(props.outstanding)}</>;
    },
  },
];

class LedgerReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      list: [],
      selectedId: '',
      selectedName: '',
      start_date: dayjs().format('YYYY-MM-DD'),
      end_date: dayjs().format('YYYY-MM-DD'),
      loading: false,
      limit: 20,
      offset: 1,
      count: 0,
      showConfirmModal: false,
      isEmailing: false,
    };
  }

  componentDidMount() {
    // No-op
  }

  componentWillUnmount() {
    // No-op
  }

  getRequestPayload = (overrides = {}) => {
    const source = this.props.source || '';

    return {
      bank_id: this.state.selectedId,
      start_date: this.state.start_date,
      end_date: this.state.end_date,
      limit: this.state.limit,
      offset: this.state.offset,
      source,
      ...overrides,
    };
  };

  getLedgerData = (requestData) => {
    return services.getLedgerReport(requestData);
  };

  getLedger = (overrides = {}) => {
    const requestData = this.getRequestPayload(overrides);
    const { offset } = requestData;

    this.setState({ loading: true });

    this.getLedgerData(requestData)
      .then((response) => {
        const results = response?.data?.data?.results || [];
        const count = response?.data?.data?.count || 0;

        this.setState((prevState) => ({
          list: offset === 1 ? results : [...prevState.list, ...results],
          count,
          loading: false,
        }));
      })
      .catch(() => {
        this.setState({ loading: false });
      });
  };

  handleLoadMore = () => {
    const { loading, count, limit, offset } = this.state;
    if (loading || count <= offset * limit) return;
    this.setState(
      (prevState) => ({ offset: prevState.offset + 1, loading: true }),
      () => this.getLedger(),
    );
  };

  handleSourceChange = (selectedOption) => {
    this.setState({
      selectedId: selectedOption?.value || '',
      selectedName: selectedOption?.label || '',
    });
  };

  handleDateChange = (dates) => {
    if (dayjs(dates[1]).isBefore(dayjs(dates[0]))) {
      toast.error('To date cannot be before the from date.');
      return;
    }

    this.setState({
      start_date: dates[0],
      end_date: dates[1],
    });
  };

  resetFilter = () => {
    this.setState(
      {
        start_date: dayjs().format('YYYY-MM-DD'),
        end_date: dayjs().format('YYYY-MM-DD'),
        loading: false,
        limit: 20,
        offset: 1,
        count: 0,
        list: [],
        showConfirmModal: false,
      },
      () => {
        if (this.selectorRef && this.selectorRef.handleReset) {
          this.selectorRef.handleReset();
        }
      },
    );
  };

  openConfirmModal = (e) => {
    e.preventDefault();

    if (!this.state.selectedId) {
      toast.error(`Select ${this.props.entityLabel.toLowerCase()} first.`);
      return;
    }

    if (dayjs(this.state.end_date).isBefore(dayjs(this.state.start_date))) {
      toast.error('To date cannot be before the from date.');
      return;
    }

    this.setState({ showConfirmModal: true });
  };

  closeConfirmModal = () => {
    if (this.state.isEmailing) {
      return;
    }

    this.setState({ showConfirmModal: false });
  };

  handleGenerateLast20 = () => {
    this.setState(
      {
        showConfirmModal: false,
        offset: 1,
        limit: 20,
      },
      () => {
        this.getLedger();
      },
    );
  };

  handleEmailReport = () => {
    if (this.state.isEmailing) {
      return;
    }

    this.setState({ isEmailing: true });

    this.getLedgerData(this.getRequestPayload({ is_download: 1, offset: 1 }))
      .then(() => {
        toast.success('Report email request submitted successfully.');
        this.setState({
          isEmailing: false,
          showConfirmModal: false,
        });
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || 'Failed to send email report.',
        );
        this.setState({ isEmailing: false });
      });
  };

  renderSummary = () => {
    const { list } = this.state;

    return (
      <>
        <div className="mini-summary">
          <ul>
            <li>
              <span> Closing balance: </span>
              <span>
                {new Intl.NumberFormat('en-IN', {}).format(
                  list[0]?.outstanding?.toFixed(2) || 0,
                )}
              </span>
            </li>
          </ul>
        </div>
        {this.props.showTotals && (
          <div className="mini-summary">
            <ul>
              <li>
                <span>Total amount credited: </span>
                <span>
                  {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                  }).format(list.reduce((a, b) => a + (b.credit || 0), 0) || 0)}
                </span>
              </li>
              <li>
                <span>Total amount debited: </span>
                <span>
                  {new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR',
                  }).format(list.reduce((a, b) => a + (b.debit || 0), 0) || 0)}
                </span>
              </li>
            </ul>
          </div>
        )}
      </>
    );
  };

  render() {
    const {
      title,
      entityLabel,
      SelectorComponent,
      selectorPlaceholder,
      showTotals,
      dateRangeLimit = 7,
      dateRangeLimitType = 'days',
    } = this.props;
    const {
      list,
      loading,
      limit,
      count,
      offset,
      start_date,
      end_date,
      selectedName,
      showConfirmModal,
      isEmailing,
    } = this.state;

    return (
      <>
        <section className="widget list-table full-width inverted">
          <header>
            <h3>{title}</h3>
          </header>
          <main>
            <div className="table-filters">
              <div className="filter-wrapper">
                <div className="filter-group">
                  <label>Date range:</label>
                  <DateRangeFilter
                    value={[start_date, end_date]}
                    onChange={this.handleDateChange}
                    limit={dateRangeLimit}
                    limitType={dateRangeLimitType}
                  />
                </div>
                <div className="filter-group">
                  <label>{entityLabel}:</label>
                  <SelectorComponent
                    handleBankChange={this.handleSourceChange}
                    value={this.state.selectedId}
                    placeholder={selectorPlaceholder}
                    ref={(ref) => {
                      this.selectorRef = ref;
                    }}
                  />
                </div>

                <div className="filter-group reset-filter">
                  <button onClick={this.resetFilter}>Reset filters</button>
                </div>
                <div className="filter-group">
                  <button
                    className={`submit ${loading ? 'loading' : ''}`}
                    disabled={loading}
                    type="submit"
                    onClick={this.openConfirmModal}
                  >
                    Generate Report
                  </button>
                </div>
              </div>
            </div>
            <div className="table-wrapper">
              {this.renderSummary()}
              {loading ? (
                <DropLoaderLarge />
              ) : (
                <TableGrid
                  col={col}
                  row={list}
                  offset={offset}
                  count={count}
                  limit={limit}
                  hideActions={true}
                  pagination="disabled"
                  enableExport={false}
                  exportOptions={{
                    fileName: `${title} - ${start_date} to ${end_date}.pdf`,
                    reportName: `${title} Report`,
                    reportDate: `Date - ${dayjs(start_date).format('D MMM YY')} to ${dayjs(end_date).format('D MMM YY')}`,
                    reportTotal: `Closing balance -  ${
                      list[0]?.outstanding?.toFixed(2) || 0
                    }`,
                    reportQuantity: '',
                    reportSales: '',
                    reportTesting: showTotals
                      ? `Total amount credited -  ${
                          list
                            .reduce((a, b) => a + (b.credit || 0), 0)
                            .toFixed(2) || 0
                        }`
                      : '',
                    reportBowser: showTotals
                      ? `Total amount debited -  ${
                          list
                            .reduce((a, b) => a + (b.debit || 0), 0)
                            .toFixed(2) || 0
                        }`
                      : '',
                    reportCustomerName: '',
                    reportGst: '',
                    reportOpeningBalance: '',
                    reportClosingBalance: '',
                    reportSaleTotal: '',
                    reportTCSTotal: '',
                  }}
                />
              )}
              {(() => {
                const hasMoreResults = count > offset * limit;
                return (
                  hasMoreResults && (
                    <div className="filter-group" style={{ marginTop: '12px' }}>
                      <button
                        className={`submit ${loading ? 'loading' : ''}`}
                        disabled={loading}
                        type="button"
                        onClick={this.handleLoadMore}
                      >
                        {loading
                          ? 'Loading...'
                          : 'Load the next 20 transactions'}
                      </button>
                    </div>
                  )
                );
              })()}
            </div>
          </main>
        </section>
        <Modal
          isOpen={showConfirmModal}
          onRequestClose={this.closeConfirmModal}
          className="sub-xl-modal-container"
          overlayClassName="xl-modal-overlay"
          contentLabel={`${title} confirmation`}
        >
          <div className="modal-component">
            <section className="widget full-width">
              <header>
                <h3>Confirm Report Details</h3>
              </header>
              <main>
                <div className="mini-summary ledger-confirmation-summary">
                  <ul>
                    <li>
                      <span className="summary-key">From Date</span>
                      <span className="summary-value">
                        {dayjs(start_date).format('DD MMM YYYY')}
                      </span>
                    </li>
                    <li>
                      <span className="summary-key">To Date</span>
                      <span className="summary-value">
                        {dayjs(end_date).format('DD MMM YYYY')}
                      </span>
                    </li>
                    <li>
                      <span className="summary-key">{entityLabel}</span>
                      <span className="summary-value">
                        {selectedName || '--'}
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="item-actions ledger-confirmation-actions">
                  <button
                    className={`submit ${isEmailing ? 'loading' : ''}`}
                    onClick={this.handleEmailReport}
                    disabled={isEmailing}
                  >
                    Email Report
                  </button>
                  <button
                    className="blue-btn"
                    onClick={this.handleGenerateLast20}
                    disabled={isEmailing}
                  >
                    Generate Last 20 Transactions
                  </button>
                  <button
                    className="red-btn"
                    onClick={this.closeConfirmModal}
                    disabled={isEmailing}
                  >
                    Cancel
                  </button>
                </div>
              </main>
            </section>
          </div>
        </Modal>
      </>
    );
  }
}

export { LedgerReport };
